import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoginAuthUserInputFields } from '@com-delta-idp/mnx-login-ui';
import {
  OauthAEMConfig,
  PkceCode,
  RememberMeWrapperService,
  UtilService
} from '@com-delta-idp/mnx-oauth';
import { FLOW_ID } from '../constants/login-constant';
import { loginAndGetDashboardResponse } from '../models/login-authentication-screen.interface';
import { PingLoginAdapterHelperService } from './ping-login-adapter-helper.service';

@Injectable({
  providedIn: 'root'
})
export class PingLoginAdapterService extends PingLoginAdapterHelperService {
  pkceCode!: PkceCode;
  authConfig!: OauthAEMConfig;
  promptCookieAvailable = false;
  redirectfulFlowId: string | undefined = '';
  utils = inject(UtilService);
  rememberMeWrapperService = inject(RememberMeWrapperService);
  route = inject(ActivatedRoute);

  checkForRedirectfulFlowAndRefreshUrl(): string {
    let param: string | null = '';
    let redirectfulFlowId = '';
    if (this.route.snapshot.queryParamMap.get(FLOW_ID)) {
      param = this.route.snapshot.queryParamMap.get(FLOW_ID);
    }
    if (param) {
      redirectfulFlowId = param ? param : '';
    }
    return redirectfulFlowId;
  }

  async processPingAutenticationStarts(config: OauthAEMConfig) {
    const promptCookie = this.cookieHelper.getCookieValue('prompt');
    const fromModal = this.coreUtilService.checkFromModalQueryParam();
    this.promptCookieAvailable = promptCookie ? true : false;
    this.redirectfulFlowId = this.checkForRedirectfulFlowAndRefreshUrl()
      ? this.checkForRedirectfulFlowAndRefreshUrl()
      : undefined;
    if (this.shouldMakePingCalls(config, fromModal)) {
      this.pkceCode = this.utils.generateCodeChallenge();
      const pingResponse = await this.rememberMeWrapperService.initiatePingFlow(
        config,
        this.pkceCode,
        undefined,
        this.redirectfulFlowId,
        !this.promptCookieAvailable
      );
      return pingResponse;
    }
    return true;
  }

  shouldMakePingCalls(config: OauthAEMConfig, fromModal: boolean) {
    return config.PING_FLOW_ENABLED && fromModal;
  }

  async authenicateUserAndGetAccessToken(
    userInputFields: LoginAuthUserInputFields | undefined,
    config: OauthAEMConfig
  ) {
    const rememberMeWrapperBeforeCred = await this.pingFlowWithCred(
      config,
      userInputFields,
      this.redirectfulFlowId
    );
    this.redirectFulFlowLogin(rememberMeWrapperBeforeCred);
    if (
      rememberMeWrapperBeforeCred.errorId === 'ERROR_MSG_RESOURCE_NOT_FOUND'
    ) {
      return await this.pingFlowWithCred(
        config,
        userInputFields,
        this.redirectfulFlowId
      );
    } else {
      return rememberMeWrapperBeforeCred;
    }
  }

  async redirectToRequestingPage(redirectUrl: string) {
    setTimeout(() => window.location.replace(`${redirectUrl}`));
  }

  async redirectFulFlowLogin(rememberMeWrapperBeforeCred: {
    status: string | undefined;
    resumeUrl: string;
  }) {
    if (rememberMeWrapperBeforeCred?.resumeUrl) {
      return await this.redirectToRequestingPage(
        rememberMeWrapperBeforeCred.resumeUrl
      );
    }
  }

  async pingFlowWithCred(
    config: OauthAEMConfig,
    requestInput?: LoginAuthUserInputFields,
    redirectfulFlowId?: string
  ) {
    if (!redirectfulFlowId) {
      return await this.rememberMeWrapperService.initiatePingFlow(
        config,
        this.pkceCode,
        requestInput
      );
    } else {
      return await this.rememberMeWrapperService.initiatePingFlow(
        config,
        this.pkceCode,
        requestInput,
        redirectfulFlowId
      );
    }
  }

  processLoginDashboardSuccessResponse(
    response: loginAndGetDashboardResponse,
    authenticationConfig?: OauthAEMConfig,
    standalonePage = false,
    tokenResponse?: string
  ): void {
    this.isStandalone = standalonePage;
    this.awsFlowEnabled =
      !!authenticationConfig?.AWS_ACCOUNT_RECOVERY_FLOW_ENABLED;
    if (this.validEmail(response)) {
      this.redirectToEmail(response, tokenResponse);
    } else if (!this.checkMfaFlowSQA(response, authenticationConfig)) {
      this.redirectSQA(response);
    } else if (response.isLoggedIn) {
      this.processRedirectionLogic(
        response,
        authenticationConfig,
        standalonePage,
        tokenResponse
      );
    }
  }
}
